import { useEffect, useRef, useState } from 'react'
import { useMedplum } from '@medplum/react'
import { logError } from '@/utils/errorUtils'
import { downloadAttachment } from '@/utils/downloadAttachment'

export type PdfViewerProps = {
  fileUrl: string
  initialPage?: number
  /** Used only when fitMode is "zoom"; the default fit-width mode ignores fixed scale. */
  initialScale?: number
  fitMode?: 'width' | 'zoom'
  className?: string
  /**
   * Told whether the document could actually be fetched. A caller that shows
   * the PDF *instead of* some fallback needs this: the attachment's content
   * type only says a PDF was filed, not that it can be read (Greptile !1606).
   */
  onLoadFailed?: (failed: boolean) => void
}

export function PdfViewer({
  fileUrl,
  initialPage = 1,
  initialScale = 1,
  fitMode = 'width',
  className = 'h-[80vh]',
  onLoadFailed,
}: PdfViewerProps) {
  const medplum = useMedplum()
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  // Held in a ref so an inline callback from the caller can't re-run the fetch.
  const onLoadFailedRef = useRef(onLoadFailed)
  onLoadFailedRef.current = onLoadFailed

  useEffect(() => {
    let cancelled = false
    let createdUrl: string | null = null
    setLoading(true)
    setFailed(false)

    downloadAttachment(medplum, fileUrl)
      .then((blob) => {
        if (cancelled) return
        createdUrl = URL.createObjectURL(blob)
        setBlobUrl(createdUrl)
        setLoading(false)
        onLoadFailedRef.current?.(false)
      })
      .catch((err) => {
        if (cancelled) return
        logError('pdf-viewer', 'Failed to load PDF document', err)
        setFailed(true)
        setLoading(false)
        onLoadFailedRef.current?.(true)
      })

    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
      setBlobUrl(null)
    }
  }, [fileUrl, medplum, retryKey])

  const zoomPercent = Math.round(initialScale * 100)
  const iframeSrc = blobUrl
    ? `${blobUrl}#page=${initialPage}&navpanes=0&${
        fitMode === 'zoom' ? `zoom=${zoomPercent}` : 'view=FitH'
      }`
    : null

  return (
    <div className={`relative w-full rounded ${className}`}>
      {loading && (
        <div className="flex items-center justify-center p-4 text-sm text-gray-500">
          Loading PDF…
        </div>
      )}
      {failed && !loading && (
        <div className="flex items-center justify-center gap-2 p-4 text-sm text-red-500">
          Failed to load PDF.
          <button
            type="button"
            className="ml-1 underline hover:opacity-80"
            onClick={() => setRetryKey((k) => k + 1)}
          >
            Retry
          </button>
        </div>
      )}
      {!loading && iframeSrc && (
        <iframe src={iframeSrc} title="PDF document" className="h-full w-full rounded border-0" />
      )}
    </div>
  )
}
